import { useEffect, useId, useRef, useState } from 'react';
import { ClipboardList, Loader2, MessageSquare, Plus, SquarePen } from 'lucide-react';
import { useSettingsStore } from '../../../lib/stores/settingsStore';
import { useActivityTypesStore } from '../../../lib/stores/activityTypesStore';
import { ComposerDateChips, ComposerTypeChips } from './ComposerActivityChips';
import {
  COMPOSER_DEFAULT_TIME,
  resolveComposerDueDate,
  toComposerDateInputValue,
} from './composerDueDate';
import { plainTextToNoteHtml } from './composerNoteHtml';
import { resolveActivityTitle } from './activityTitleFromDescription';
import { useBookingRule } from '../../../lib/calendar/useBookingRule';

export type ComposerMode = 'note' | 'activity';

export interface ComposerDrawerPayload {
  /** Modalità Nota: HTML del testo digitato, da pre-popolare nell'editor del drawer. */
  noteText?: string;
  /** Modalità Attività: titolo, tipo, scadenza e descrizione già scelti. */
  title?: string;
  type?: string;
  dueDate?: Date;
  description?: string;
}

export interface InlineComposerProps {
  /** Salvataggio della nota: HTML già pronto (stesso formato dell'editor del drawer). */
  onSaveNote(html: string): Promise<void> | void;
  /** Creazione dell'attività veloce: nessun calendario, nessun invitato (regola del design). */
  onSaveActivity(input: { title: string; type: string; dueDate: Date; description?: string }): Promise<void> | void;
  /** Escalation: apre il drawer completo della scheda portandosi dietro il contenuto. */
  onOpenDrawer(mode: ComposerMode, payload: ComposerDrawerPayload): void;
  /** Disabilita l'intero composer (entità non ancora caricata, permessi, ...). */
  disabled?: boolean;
  className?: string;
}

const SEGMENTED_BASE =
  'inline-flex items-center gap-1 text-[11px] px-2.5 py-[3px] rounded transition-colors';
const SEGMENTED_ON = 'bg-[#2383E2] text-white font-semibold';
const SEGMENTED_OFF = 'text-[#787774] hover:text-[#37352F] dark:text-gray-400 dark:hover:text-white';

/**
 * Composer inline sopra la cronologia (lead e offerta): riga ghost collassata che si apre
 * in una card con UN solo input, il cui significato segue il mini-segmented Nota | Attività
 * (il testo digitato si CONSERVA al cambio modalità).
 *
 * Pattern di interazione ripreso da `dashboard/InlineActivityInput.tsx` (chip tipo con
 * colori, scorciatoie data, Invio/Esc, reset che resta espanso con focus sull'input); qui
 * NON c'è il picker cliente (l'entità è la scheda) e soprattutto NESSUNA scrittura: i
 * salvataggi arrivano dalle props, così il composer usa gli STESSI percorsi di persistenza
 * del drawer (adapter della nota, `useActivitySave` per l'attività).
 *
 * REGOLA di design: l'attività veloce non tocca mai il calendario (niente sync, invitati o
 * Meet). Per quello si usa "Apri nel drawer completo", che porta con sé il contenuto.
 *
 * Riferimento visivo: MD/features/2026-08-10-drawer-note-ui-ux-redesign-mockup.html,
 * viste 5 (collassato), 6 e 6b (espanso).
 */
export function InlineComposer({
  onSaveNote,
  onSaveActivity,
  onOpenDrawer,
  disabled = false,
  className,
}: InlineComposerProps) {
  const { settings } = useSettingsStore();
  const { customTypes, fetched: customTypesFetched, fetchCustomTypes } = useActivityTypesStore();
  const fieldIdPrefix = `inline-composer-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const defaultType = (settings?.activity_default_type || 'task') as string;

  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState<ComposerMode>('note');
  const [text, setText] = useState('');
  const [description, setDescription] = useState('');
  const [selectedType, setSelectedType] = useState<string>(defaultType);
  const [offsetDays, setOffsetDays] = useState<number | null>(0);
  const [customDate, setCustomDate] = useState('');
  const [time, setTime] = useState(COMPOSER_DEFAULT_TIME);
  const [saving, setSaving] = useState(false);
  // Messaggio mostrato quando si prova a salvare senza contenuto (prima il pulsante
  // restava semplicemente fermo e l'utente non capiva perché).
  const [emptyHint, setEmptyHint] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  // Guardia SINCRONA contro il doppio Invio: `saving` è stato React e diventa true solo al
  // render successivo, quindi due Invio ravvicinati passerebbero entrambi la guardia e
  // salverebbero due volte.
  const savingRef = useRef(false);
  // Il focus va rimesso quando l'input torna ABILITATO: farlo subito dopo il salvataggio,
  // con `disabled` ancora true, non ha alcun effetto (M1).
  const refocusAfterSaveRef = useRef(false);

  useEffect(() => {
    if (saving || !refocusAfterSaveRef.current) return;
    refocusAfterSaveRef.current = false;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [saving]);

  // Il messaggio "manca il contenuto" vale per la modalità in cui è nato
  useEffect(() => {
    setEmptyHint(null);
  }, [mode]);

  useEffect(() => {
    if (!customTypesFetched) fetchCustomTypes();
  }, [customTypesFetched, fetchCustomTypes]);

  // Il tipo di default dell'org arriva in modo asincrono: si adegua finché l'utente non ha
  // aperto il composer (dopo, una sua scelta esplicita non va sovrascritta).
  useEffect(() => {
    if (!expanded) setSelectedType(defaultType);
  }, [defaultType, expanded]);

  // Regola "Fissare gli appuntamenti" (Impostazioni › Calendario): l'attivita' veloce non sceglie
  // la persona, quindi un appuntamento soggetto alla regola si completa nel pannello completo.
  const bookingRule = useBookingRule(expanded && mode === 'activity' ? selectedType : null, null);
  const needsDrawer = !!bookingRule;

  const activeCustomTypes = customTypes.filter((type) => type.active);
  const trimmed = text.trim();
  // In modalità Attività basta anche la sola descrizione: il titolo diventa la sua prima riga.
  const hasContent = trimmed.length > 0 || (mode === 'activity' && description.trim().length > 0);
  const canSave = hasContent && !saving && !disabled;

  const resetFields = () => {
    setEmptyHint(null);
    setText('');
    setDescription('');
    setSelectedType(defaultType);
    setOffsetDays(0);
    setCustomDate('');
    setTime(COMPOSER_DEFAULT_TIME);
  };

  const expand = (nextMode: ComposerMode) => {
    setMode(nextMode);
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const collapse = () => {
    setExpanded(false);
    resetFields();
  };

  const buildDueDate = () => resolveComposerDueDate({ offsetDays, customDate, time });

  const handleSave = async () => {
    if (saving || disabled || savingRef.current) return;
    const activityTitle = mode === 'activity' ? resolveActivityTitle(text, description) : '';
    if (mode === 'note' ? !trimmed : !activityTitle) {
      setEmptyHint(
        mode === 'note'
          ? 'Scrivi il testo della nota prima di salvare.'
          : "Scrivi un titolo (o una descrizione) per salvare l'attività.",
      );
      inputRef.current?.focus();
      return;
    }
    if (mode === 'activity' && needsDrawer) {
      handleOpenDrawer();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      if (mode === 'note') {
        const html = plainTextToNoteHtml(trimmed);
        if (!html) return;
        await onSaveNote(html);
      } else {
        await onSaveActivity({
          title: activityTitle,
          type: selectedType,
          dueDate: buildDueDate(),
          description: description.trim() || undefined,
        });
      }
      // Come nella Dashboard: si svuota il contenuto ma si resta espansi, col focus
      // sull'input, per incatenare più inserimenti di seguito. Il focus è deferito
      // all'effect: qui l'input è ancora disabilitato.
      resetFields();
      refocusAfterSaveRef.current = true;
    } catch (error) {
      // Il toast d'errore lo emette già il percorso di salvataggio: qui si evita solo il
      // reset, così l'utente ritrova il testo digitato e può ritentare.
      console.error('[InlineComposer] salvataggio fallito:', error);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleOpenDrawer = () => {
    const payload: ComposerDrawerPayload =
      mode === 'note'
        ? { noteText: plainTextToNoteHtml(trimmed) }
        : {
            title: trimmed,
            type: selectedType,
            dueDate: buildDueDate(),
            description: description.trim() || undefined,
          };
    // Il contenuto vive ora nel drawer: il composer si chiude scartando il proprio stato.
    collapse();
    onOpenDrawer(mode, payload);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSave();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      // Escape qui collassa SOLO il composer: senza stop l'evento risalirebbe anche al
      // modale che ospita la scheda (vista modale del lead), chiudendola per sbaglio.
      event.stopPropagation();
      collapse();
    }
  };

  if (!expanded) {
    return (
      <button
        type="button"
        data-tour="timeline-composer"
        disabled={disabled}
        onClick={() => expand('note')}
        className={`w-full flex items-center gap-2 px-2.5 py-2 mb-3 rounded-lg text-[13px] text-left transition-colors text-[rgba(55,53,47,0.45)] hover:bg-[rgba(55,53,47,0.05)] hover:text-[#787774] dark:text-white/40 dark:hover:bg-white/[0.06] dark:hover:text-gray-300 disabled:cursor-not-allowed disabled:opacity-60 ${className || ''}`}
      >
        <Plus className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
        Scrivi una nota o pianifica un'attività...
      </button>
    );
  }

  return (
    <div data-tour="timeline-composer" className={`mb-3 ${className || ''}`}>
    <div
      className="rounded-lg border bg-white border-[rgba(55,53,47,0.16)] dark:bg-[#2F3437] dark:border-[#4A5053]"
    >
      <input
        ref={inputRef}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          if (emptyHint) setEmptyHint(null);
        }}
        onKeyDown={handleKeyDown}
        disabled={saving || disabled}
        placeholder={mode === 'note' ? 'Scrivi una nota...' : 'Cosa devi fare?'}
        className="w-full bg-transparent outline-none px-3 pt-2.5 pb-1 text-[13px] text-[#37352F] placeholder-[rgba(55,53,47,0.45)] dark:text-white dark:placeholder-white/40"
      />

      {/* Descrizione facoltativa, solo in modalità Attività. Invio qui NON salva
          (textarea multiriga); Esc collassa come sull'input del titolo. */}
      {mode === 'activity' && (
        <textarea
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
            if (emptyHint) setEmptyHint(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              collapse();
            }
          }}
          disabled={saving || disabled}
          rows={2}
          placeholder="Aggiungi una descrizione (facoltativa)..."
          className="w-full bg-transparent outline-none resize-none px-3 pb-1 text-[12px] leading-relaxed text-[#37352F] placeholder-[rgba(55,53,47,0.35)] dark:text-white dark:placeholder-white/30"
        />
      )}

      {emptyHint && (
        <p role="alert" className="px-3 pb-1 text-[11px] text-red-500">
          {emptyHint}
        </p>
      )}

      <div className="flex items-center gap-1.5 px-2.5 pb-2 pt-1 flex-wrap">
        <span className="inline-flex items-center gap-0.5 mr-1 p-[2px] rounded-md border border-[rgba(55,53,47,0.16)] dark:border-[#4A5053]">
          <button
            type="button"
            onClick={() => setMode('note')}
            disabled={saving || disabled}
            className={`${SEGMENTED_BASE} ${mode === 'note' ? SEGMENTED_ON : SEGMENTED_OFF} disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <MessageSquare className="h-3 w-3" strokeWidth={1.5} />
            Nota
          </button>
          <button
            type="button"
            onClick={() => setMode('activity')}
            disabled={saving || disabled}
            className={`${SEGMENTED_BASE} ${mode === 'activity' ? SEGMENTED_ON : SEGMENTED_OFF} disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <ClipboardList className="h-3 w-3" strokeWidth={1.5} />
            Attività
          </button>
        </span>
      </div>

      {/* Chip tipo su riga dedicata con titoletto a colonna (come le righe di proprietà
          del drawer): col segmented sulla stessa riga andavano a capo male */}
      {mode === 'activity' && (
        <div className="flex items-start gap-2 px-2.5 pb-2">
          <span className="w-[92px] shrink-0 pt-[4px] text-[11px] text-[#787774] dark:text-gray-400">
            Tipo di attività
          </span>
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            <ComposerTypeChips
              selectedType={selectedType}
              onSelect={setSelectedType}
              customTypes={activeCustomTypes}
              disabled={saving || disabled}
            />
          </div>
        </div>
      )}

      {mode === 'activity' && (
        <div className="flex items-start gap-2 px-2.5 pb-2">
          <span className="w-[92px] shrink-0 pt-[4px] text-[11px] text-[#787774] dark:text-gray-400">
            Quando
          </span>
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <ComposerDateChips
            fieldIdPrefix={fieldIdPrefix}
            offsetDays={offsetDays}
            customDate={customDate}
            time={time}
            onOffsetChange={(days) => {
              setOffsetDays(days);
              setCustomDate('');
            }}
            onCustomDateChange={(value) => {
              setCustomDate(value);
              setOffsetDays(null);
            }}
            onTimeChange={(value) => setTime(value || COMPOSER_DEFAULT_TIME)}
            disabled={saving || disabled}
            minDate={toComposerDateInputValue(new Date())}
          />
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
        <button
          type="button"
          onClick={handleOpenDrawer}
          disabled={saving || disabled}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] text-[#787774] hover:bg-[rgba(55,53,47,0.05)] hover:text-[#37352F] dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-white transition-colors"
        >
          <SquarePen className="h-3 w-3" strokeWidth={1.5} />
          Vedi tutte le opzioni
        </button>
        <span className="flex-1" />
        <button
          type="button"
          onClick={collapse}
          disabled={saving || disabled}
          className="px-2.5 py-1 rounded text-[11px] font-medium text-[#787774] hover:bg-[rgba(55,53,47,0.05)] hover:text-[#37352F] dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-white transition-colors"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || disabled}
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-medium transition-colors ${
            canSave
              ? 'bg-[#37352F] text-white hover:bg-[#2F2E2B] dark:bg-white dark:text-[#37352F] dark:hover:bg-gray-200'
              : 'bg-[rgba(55,53,47,0.08)] text-[rgba(55,53,47,0.45)] cursor-not-allowed dark:bg-white/[0.08] dark:text-white/40'
          }`}
        >
          {saving && <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />}
          {mode === 'note' ? 'Salva nota' : needsDrawer ? 'Scegli la persona' : 'Salva attività'}
        </button>
      </div>
    </div>

      {/* Riga hint sotto la card (mockup, `.cmp-hint`). Il "·" è un glifo dato, non un em dash. */}
      <p className="px-1 pt-1 text-[10px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
        {mode === 'activity' && needsDrawer
          ? "Per questo appuntamento si sceglie nel calendario di chi va: si apre il pannello completo"
          : 'Invio salva · Esc annulla'}
      </p>
    </div>
  );
}

export default InlineComposer;
